import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  TrendingUp,
  AlertTriangle,
  FileBarChart,
  Calendar,
  RefreshCw,
  Download,
  X,
  ChevronRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Report, Company, CompanyPerformanceReport } from '../types';
import apiService from '../services/api';
import { formatDateTime } from '../utils/timeUtils';

const ReportsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [reports, setReports] = useState<CompanyPerformanceReport[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    reportType: 'company',
    startDate: '',
    endDate: '',
    companyId: '',
  });

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch all company performance reports
      const data = await apiService.getCompanyPerformanceReports();

      // Map the backend response to match frontend expectations
      const mappedData = data.map((report: any) => ({
        ...report,
        period: {
          start: report.period?.startDate || report.period?.start,
          end: report.period?.endDate || report.period?.end,
        },
        unresolvedComplaints: report.unresolvedCount ?? report.unresolvedComplaints ?? 0,
        slaPerformance: {
          total: report.slaPerformance?.total ?? 0,
          onTime: report.slaPerformance?.onTime ?? 0,
          percentage: parseFloat(report.slaPerformance?.percentage || '0'),
        },
        deductions: {
          totalAmount: report.deductions?.total ?? report.deductions?.totalAmount ?? 0,
          deductionCount: report.deductions?.count ?? report.deductions?.deductionCount ?? 0,
          items: report.deductions?.items || [],
        },
      }));

      setReports(mappedData);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const data = await apiService.getCompanies();
      setCompanies(data);
    } catch (err) {
      // Failed to fetch companies
    }
  }, []);

  useEffect(() => {
    fetchReports();
    fetchCompanies();
  }, [fetchReports, fetchCompanies]);

  const handleGenerateReport = async () => {
    if (!formData.startDate || !formData.endDate) {
      setError(isRTL ? 'يرجى تحديد التاريخ' : 'Please select dates');
      return;
    }

    try {
      setGenerating(true);
      setError('');
      setSuccess('');

      // Determine which API to call based on report type
      if (formData.reportType === 'company' && formData.companyId) {
        // Generate company performance report
        const startDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);
        const year = startDate.getFullYear();
        const month = startDate.getMonth() + 1;

        await apiService.getCompanyMonthlyReport(formData.companyId, year, month);
      } else if (formData.reportType === 'performance') {
        // Get all companies performance comparison
        const startDate = new Date(formData.startDate);
        const year = startDate.getFullYear();
        const month = startDate.getMonth() + 1;

        await apiService.getCompanyPerformanceComparison(year, month);
      } else {
        // For other report types, fetch general company performance reports
        await apiService.getCompanyPerformanceReports({
          startDate: formData.startDate,
          endDate: formData.endDate,
        });
      }

      setSuccess(isRTL ? 'تم إنشاء التقرير بنجاح' : 'Report generated successfully');
      await fetchReports();
    } catch (err: any) {
      setError(err.response?.data?.message || (isRTL ? 'فشل إنشاء التقرير' : 'Failed to generate report'));
    } finally {
      setGenerating(false);
    }
  };

  const quickReportCards = [
    {
      title: isRTL ? 'تقرير الشهر الحالي' : 'Current Month Report',
      description: isRTL ? 'إنشاء سريع' : 'Quick Generate',
      icon: FileText,
      gradient: 'from-brand-500 to-brand-600',
      iconBg: 'bg-brand-100',
      iconColor: 'text-brand-600',
    },
    {
      title: isRTL ? 'خروقات SLA' : 'SLA Breaches',
      description: isRTL ? 'آخر 30 يوم' : 'Last 30 Days',
      icon: AlertTriangle,
      gradient: 'from-red-500 to-red-600',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
    },
    {
      title: isRTL ? 'تقرير الأداء' : 'Performance Report',
      description: isRTL ? 'جميع الشركات' : 'All Companies',
      icon: TrendingUp,
      gradient: 'from-emerald-500 to-emerald-600',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      title: isRTL ? 'تقرير مخصص' : 'Custom Report',
      description: isRTL ? 'اختر الفترة' : 'Select Period',
      icon: FileBarChart,
      gradient: 'from-amber-500 to-amber-600',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-green-50 to-emerald-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Modern Header */}
        <div className="relative overflow-hidden rounded-3xl">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 opacity-95"></div>
          <div className="absolute inset-0 backdrop-blur-3xl bg-white/10"></div>

          <div className="relative px-6 py-8 md:px-10 md:py-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-white/30 rounded-2xl blur-xl"></div>
                  <div className="relative bg-white/20 backdrop-blur-md p-4 rounded-2xl border border-white/30 shadow-2xl">
                    <FileBarChart className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
                    {isRTL ? 'التقارير' : 'Reports'}
                  </h1>
                  <p className="text-white/80 text-sm md:text-base font-medium mt-1">
                    {isRTL ? 'إنشاء وإدارة التقارير التحليلية' : 'Generate and manage analytics reports'}
                  </p>
                </div>
              </div>

              <button
                onClick={fetchReports}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-md text-white rounded-xl
                         hover:bg-white/30 transition-all border border-white/30 font-semibold disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                <span>{isRTL ? 'تحديث' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error/Success Alerts */}
        {error && (
          <div className="bg-gradient-to-r from-red-50 to-rose-50 border-r-4 border-red-500 rounded-2xl p-5 shadow-lg animate-shake">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-red-100 p-2 rounded-xl">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h4 className="text-red-900 font-bold text-lg mb-1">{isRTL ? 'تنبيه' : 'Notice'}</h4>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </div>
              <button
                onClick={() => setError('')}
                className="flex-shrink-0 text-red-400 hover:text-red-600 hover:bg-red-100 p-2 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-r-4 border-emerald-500 rounded-2xl p-5 shadow-lg">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-emerald-100 p-2 rounded-xl">
                  <FileText className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h4 className="text-emerald-900 font-bold text-lg mb-1">{isRTL ? 'نجح' : 'Success'}</h4>
                  <p className="text-emerald-700 font-medium">{success}</p>
                </div>
              </div>
              <button
                onClick={() => setSuccess('')}
                className="flex-shrink-0 text-emerald-400 hover:text-emerald-600 hover:bg-emerald-100 p-2 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Generate New Report Card */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
          <div className="bg-gradient-to-r from-brand-500 to-brand-600 px-8 py-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <FileText className="w-6 h-6" />
              {isRTL ? 'إنشاء تقرير جديد' : 'Generate New Report'}
            </h2>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Report Type */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {isRTL ? 'نوع التقرير' : 'Report Type'}
                </label>
                <select
                  value={formData.reportType}
                  onChange={(e) => setFormData({ ...formData, reportType: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl
                           focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all
                           text-gray-900 font-medium outline-none"
                >
                  <option value="monthly">{isRTL ? 'تقرير شهري' : 'Monthly Report'}</option>
                  <option value="company">{isRTL ? 'تقرير الشركة' : 'Company Report'}</option>
                  <option value="sla_breaches">{isRTL ? 'خروقات SLA' : 'SLA Breaches'}</option>
                  <option value="performance">{isRTL ? 'تقرير الأداء' : 'Performance Report'}</option>
                </select>
              </div>

              {/* From Date */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {isRTL ? 'من' : 'From'}
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl
                           focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all
                           text-gray-900 font-medium outline-none"
                />
              </div>

              {/* To Date */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {isRTL ? 'إلى' : 'To'}
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl
                           focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all
                           text-gray-900 font-medium outline-none"
                />
              </div>

              {/* Company (Optional) */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  {isRTL ? 'الشركة (اختياري)' : 'Company (Optional)'}
                </label>
                <select
                  value={formData.companyId}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl
                           focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all
                           text-gray-900 font-medium outline-none"
                >
                  <option value="">{isRTL ? 'جميع الشركات' : 'All Companies'}</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.companyName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Generate Button */}
              <div className="md:col-span-2 lg:col-span-4">
                <button
                  onClick={handleGenerateReport}
                  disabled={generating}
                  className="w-full px-8 py-4 bg-gradient-to-r from-brand-600 via-brand-700 to-brand-800
                           text-white rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl
                           transform hover:scale-[1.02] active:scale-95 transition-all
                           disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none
                           flex items-center justify-center gap-3 border-b-4 border-brand-900"
                >
                  {generating ? (
                    <>
                      <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{isRTL ? 'جاري الإنشاء...' : 'Generating...'}</span>
                    </>
                  ) : (
                    <>
                      <FileBarChart className="w-6 h-6" />
                      <span>{isRTL ? 'إنشاء التقرير' : 'GENERATE REPORT'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Report Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickReportCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={index}
                className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden
                         hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              >
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-5 transition-opacity`}></div>

                <div className="relative p-6 space-y-4">
                  {/* Icon */}
                  <div className={`w-16 h-16 ${card.iconBg} rounded-2xl flex items-center justify-center
                                group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-8 h-8 ${card.iconColor}`} />
                  </div>

                  {/* Content */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-brand-700 transition-colors">
                      {card.title}
                    </h3>
                    <p className="text-sm text-gray-600 font-medium">
                      {card.description}
                    </p>
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center justify-end">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center
                                  group-hover:bg-brand-100 transition-colors">
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-brand-600 transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reports History */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-50 to-white px-8 py-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Calendar className="w-6 h-6 text-brand-600" />
                {isRTL ? 'سجل التقارير' : 'Report History'}
              </h2>
              <span className="px-4 py-2 bg-brand-100 text-brand-800 rounded-full text-sm font-bold">
                {reports.length} {isRTL ? 'تقرير' : 'Reports'}
              </span>
            </div>
          </div>

          {reports.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {isRTL ? 'لا توجد تقارير' : 'No Reports Yet'}
              </h3>
              <p className="text-gray-600">
                {isRTL ? 'ابدأ بإنشاء تقرير جديد' : 'Start by generating a new report'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700">
                    <th className="px-6 py-4 text-left text-sm font-black text-white uppercase tracking-wide">
                      {isRTL ? 'الشركة' : 'Company'}
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide">
                      {isRTL ? 'الفترة' : 'Period'}
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide">
                      {isRTL ? 'مجموع البلاغات' : 'Total Complaints'}
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide">
                      {isRTL ? 'البلاغات المكتملة' : 'Completed'}
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide">
                      {isRTL ? 'نسبة SLA' : 'SLA %'}
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide">
                      {isRTL ? 'الخصومات' : 'Deductions'}
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide">
                      {isRTL ? 'الإجراءات' : 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reports.map((report, index) => (
                    <tr key={report.companyId || index} className="hover:bg-brand-50/30 transition-all group">
                      <td className="px-6 py-4">
                        <span className="font-bold text-brand-700">{report.companyName}</span>
                      </td>
                      <td className="px-6 py-4 text-center text-gray-700 font-medium">
                        {new Date(report.period.start).toLocaleDateString()} - {new Date(report.period.end).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-bold">
                          {report.totalComplaints}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-bold">
                          {report.completedComplaints}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold ${
                          Number(report.slaPerformance?.percentage || 0) >= 80
                            ? 'bg-green-100 text-green-800'
                            : Number(report.slaPerformance?.percentage || 0) >= 60
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {Number(report.slaPerformance?.percentage || 0).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center text-gray-700 font-medium">
                        {Number(report.deductions?.totalAmount || 0).toFixed(2)} {isRTL ? 'رق' : 'QAR'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => {
                              // Download PDF functionality
                              const year = new Date(report.period.start).getFullYear();
                              const month = new Date(report.period.start).getMonth() + 1;
                              apiService.downloadCompanyMonthlyReportPDF(report.companyId, year, month)
                                .then(blob => {
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `company-report-${report.companyName}-${year}-${month}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                })
                                .catch(err => {
                                  console.error('Failed to download PDF:', err);
                                  setError(isRTL ? 'فشل تحميل PDF' : 'Failed to download PDF');
                                });
                            }}
                            className="p-2 bg-brand-100 text-brand-700 rounded-lg hover:bg-brand-200 transition-all"
                            title={isRTL ? 'تحميل' : 'Download'}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
